import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

import { LogEntry } from '../types';

type InsightTimeBucket = 'Dawn' | 'Morning' | 'Midday' | 'Evening' | 'Night';

export type RoddoInsightCatchData = Pick<
  LogEntry,
  'species' | 'time' | 'date' | 'location' | 'lureOrBait' | 'lureOrBaitType' | 'technique' | 'weather'
> & {
  timestamp?: number;
  waterCondition?: string;
  conditions?: string;
  communityPostId?: string | null;
  isShared?: boolean;
  shareToCommunity?: boolean;
};

interface RoddoInsightCardProps {
  open: boolean;
  catchEntry: RoddoInsightCatchData | null;
  onClose: () => void;
  nearbyCatchCount?: number;
  weatherText?: string | null;
  onDisablePostCatchInsight?: () => void | Promise<void>;
}

const SPECIES_LURE_MAP: Array<{ match: RegExp; lure: string }> = [
  { match: /bass|largemouth|smallmouth|spotted/i, lure: 'Run soft plastics near shade, docks, or cover' },
  { match: /trout|rainbow|brown|brook/i, lure: 'Try a small spoon or spinner through moving water' },
  { match: /catfish|channel|blue cat|flathead/i, lure: 'Soak cut bait near deeper edges' },
  { match: /crappie|bluegill|panfish|sunfish/i, lure: 'Work a tiny jig near structure' },
  { match: /walleye/i, lure: 'Probe low-light drop-offs with a jig' },
  { match: /pike|musky|muskie/i, lure: 'Flash a spinnerbait along weed edges' },
  { match: /salmon|steelhead/i, lure: 'Drift or swing a spoon through current seams' },
  { match: /redfish|red drum|snook|tarpon|speckled|seatrout/i, lure: 'Swim a paddle tail around tide movement' },
];

const resolvePortalDarkMode = () => {
  if (typeof document === 'undefined') return false;
  const shell = document.getElementById('roddo-app-shell');
  const theme =
    shell?.getAttribute('data-roddo-theme') ||
    document.documentElement.getAttribute('data-roddo-theme') ||
    document.body?.getAttribute('data-roddo-theme') ||
    document.documentElement.getAttribute('data-theme') ||
    document.body?.getAttribute('data-theme') ||
    '';

  if (String(theme).toLowerCase() === 'dark') return true;
  if (String(theme).toLowerCase() === 'light') return false;

  return (
    document.documentElement.classList.contains('dark') ||
    document.body?.classList.contains('dark') ||
    window.matchMedia?.('(prefers-color-scheme: dark)').matches === true
  );
};

function getCatchDate(catchEntry: RoddoInsightCatchData): Date {
  if (typeof catchEntry.timestamp === 'number' && Number.isFinite(catchEntry.timestamp)) {
    return new Date(catchEntry.timestamp);
  }

  if (catchEntry.date && catchEntry.time) {
    const parsed = new Date(`${catchEntry.date}T${catchEntry.time}`);
    if (!Number.isNaN(parsed.getTime())) return parsed;
  }

  return new Date();
}

function formatCatchTime(catchEntry: RoddoInsightCatchData): string {
  const d = getCatchDate(catchEntry);
  return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

function getBucket(date: Date): InsightTimeBucket {
  const hour = date.getHours();
  if (hour >= 4 && hour < 7) return 'Dawn';
  if (hour >= 7 && hour < 11) return 'Morning';
  if (hour >= 11 && hour < 17) return 'Midday';
  if (hour >= 17 && hour < 21) return 'Evening';
  return 'Night';
}

function getNextWindow(date: Date, bucket: InsightTimeBucket): string {
  const next = new Date(date);

  if (bucket === 'Dawn' || bucket === 'Morning') {
    next.setHours(17, 30, 0, 0);
    return `Today ${next.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}–7:00 PM`;
  }

  next.setDate(next.getDate() + 1);
  next.setHours(5, 40, 0, 0);
  return `Tomorrow ${next.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}–7:10 AM`;
}

function getLureSuggestion(species: string, fallback?: string): string {
  const cleanFallback = fallback?.trim();
  if (cleanFallback) return `Run it back with ${cleanFallback}`;

  const found = SPECIES_LURE_MAP.find((item) => item.match.test(species));
  return found?.lure ?? 'Match the hatch near structure';
}

function buildPatternLines(args: {
  bucket: InsightTimeBucket;
  weatherText?: string | null;
  waterCondition?: string;
  nearbyCatchCount: number;
}): string[] {
  const lines: string[] = [];
  const weather = (args.weatherText ?? '').toLowerCase();
  const water = (args.waterCondition ?? '').toLowerCase();

  if (args.bucket === 'Dawn') lines.push('Dawn bite logged — shallow feeding may repeat');
  if (args.bucket === 'Morning') lines.push('Morning bite logged — keep moving until the pattern repeats');
  if (args.bucket === 'Midday') lines.push('Midday catch logged — check shade, cover, and deeper edges');
  if (args.bucket === 'Evening') lines.push('Evening window logged — low light may be turning fish on');
  if (args.bucket === 'Night') lines.push('Night catch logged — save this as a repeat low-light clue');

  if (/cloud|overcast|fog|mist|rain|drizzle/.test(weather)) {
    lines.push('Low light weather may be helping activity');
  }

  if (/wind|breeze/.test(weather)) {
    lines.push('Wind may be pushing bait — scan wind-blown banks next');
  }

  if (/clear|stained|muddy|current|chop|calm/.test(water)) {
    lines.push(`${args.waterCondition} water noted — compare it against the next catch`);
  }

  if (args.nearbyCatchCount > 0) {
    lines.push(
      args.nearbyCatchCount === 1
        ? 'Nearby activity detected in the last 24h'
        : `Nearby activity spike detected (${args.nearbyCatchCount} recent catches)`,
    );
  }

  return Array.from(new Set(lines)).slice(0, 3);
}

const RoddoInsightCard: React.FC<RoddoInsightCardProps> = ({
  open,
  catchEntry,
  onClose,
  nearbyCatchCount = 0,
  weatherText,
  onDisablePostCatchInsight,
}) => {
  const [disableAfterCatch, setDisableAfterCatch] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);

  useEffect(() => {
    if (open) setDisableAfterCatch(false);
  }, [open, catchEntry]);

  useEffect(() => {
    if (!open || typeof window === 'undefined') return;

    const syncTheme = () => setIsDarkMode(resolvePortalDarkMode());
    syncTheme();

    const observer = typeof MutationObserver !== 'undefined'
      ? new MutationObserver(() => window.requestAnimationFrame(syncTheme))
      : null;

    observer?.observe(document.documentElement, { attributes: true, attributeFilter: ['class', 'data-theme', 'data-roddo-theme'] });
    if (document.body) observer?.observe(document.body, { attributes: true, attributeFilter: ['class', 'data-theme', 'data-roddo-theme'] });
    const shell = document.getElementById('roddo-app-shell');
    if (shell) observer?.observe(shell, { attributes: true, attributeFilter: ['class', 'data-theme', 'data-roddo-theme'] });

    window.addEventListener('themechange', syncTheme as EventListener);
    window.addEventListener('storage', syncTheme as EventListener);
    window.addEventListener('focus', syncTheme as EventListener);

    return () => {
      observer?.disconnect();
      window.removeEventListener('themechange', syncTheme as EventListener);
      window.removeEventListener('storage', syncTheme as EventListener);
      window.removeEventListener('focus', syncTheme as EventListener);
    };
  }, [open]);

  const handleDisableToggle = async (checked: boolean) => {
    setDisableAfterCatch(checked);
    if (!checked || !onDisablePostCatchInsight) return;

    try {
      await onDisablePostCatchInsight();
    } catch (error) {
      console.error('Failed to disable post-catch insight', error);
      setDisableAfterCatch(false);
    }
  };

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onClose]);

  const insight = useMemo(() => {
    if (!catchEntry) return null;

    const date = getCatchDate(catchEntry);
    const bucket = getBucket(date);
    const waterCondition = catchEntry.waterCondition || catchEntry.conditions || '';
    const patternLines = buildPatternLines({
      bucket,
      weatherText: weatherText || catchEntry.weather || null,
      waterCondition,
      nearbyCatchCount,
    });

    return {
      bucket,
      catchTime: formatCatchTime(catchEntry),
      location: catchEntry.location?.trim() || 'Saved spot',
      patternLines,
      lure: getLureSuggestion(catchEntry.species || 'Fish', catchEntry.lureOrBait),
      nextWindow: getNextWindow(date, bucket),
      wasShared: Boolean(catchEntry.communityPostId || catchEntry.isShared || catchEntry.shareToCommunity),
    };
  }, [catchEntry, nearbyCatchCount, weatherText]);

  if (!open || !catchEntry || !insight) return null;

  const shellClass = isDarkMode
    ? 'border-[rgba(180,195,220,.14)] bg-[#0A1120] text-[#F4F6FA] shadow-[0_12px_32px_rgba(0,0,0,0.34)]'
    : 'border-[rgba(74,58,42,.14)] bg-[#F7F2E8] text-[#30271E] shadow-[0_18px_44px_rgba(61,44,25,0.20)]';
  const innerClass = isDarkMode
    ? 'border-[rgba(180,195,220,.12)] bg-[#101827]'
    : 'border-[rgba(74,58,42,.12)] bg-[#FFFCF7]';
  const softCardClass = isDarkMode
    ? 'border-[rgba(180,195,220,.12)] bg-[#0F1828]'
    : 'border-[rgba(74,58,42,.12)] bg-[#F2EBE1]';
  const mutedText = isDarkMode ? 'text-[#AEB8C9]' : 'text-[#6b6258]/86';
  const strongText = isDarkMode ? 'text-white' : 'text-[#26323b]';

  return createPortal(
    <div className="fixed inset-0 z-[2147483000] flex items-end justify-center overflow-y-auto bg-[#070B15]/78 px-3 pb-[calc(env(safe-area-inset-bottom)+14px)] pt-[calc(env(safe-area-inset-top)+14px)] backdrop-blur-[10px] sm:items-center sm:p-6">
      <style>{`
        @keyframes roddoInsightIn {
          0% { opacity: 0; transform: translateY(18px) scale(.965); filter: blur(6px); }
          100% { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); }
        }
        @keyframes roddoInsightFloat {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-3px); }
        }
      `}</style>

      <div className="pointer-events-none absolute inset-0 bg-[#070B15]/38" />

      <section
        role="dialog"
        aria-modal="true"
        aria-label="Catch saved insight"
        style={{ animation: 'roddoInsightIn 260ms cubic-bezier(.2,.9,.2,1) both' }}
        className={`relative w-full max-w-[430px] overflow-hidden rounded-[20px] border p-[1px] backdrop-blur-2xl ${shellClass}`}
      >
        <div className={`pointer-events-none absolute inset-x-0 top-0 h-px ${isDarkMode ? 'bg-[#8A70F2]/55' : 'bg-[#2F80ED]/40'}`} />

        <div className={`relative rounded-[19px] border p-4 sm:p-5 ${innerClass}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className={`text-[11px] font-black uppercase tracking-[0.28em] ${isDarkMode ? 'text-[#C9C2FF]' : 'text-[#2168C9]'}`}>
                ✅ Catch saved
              </p>
              <h2 className={`mt-1 text-2xl font-black leading-tight tracking-[-0.04em] ${strongText}`}>
                Nice log, Captain 🎣
              </h2>
              <p className={`mt-2 text-sm font-bold leading-relaxed ${mutedText}`}>
                Roddo stored this catch, updated your fishing record, and turned it into a useful clue for next time.
              </p>
            </div>

            <button
              type="button"
              onClick={onClose}
              className={`grid h-10 w-10 shrink-0 place-items-center rounded-2xl border text-sm font-black ${
                isDarkMode
                  ? 'border-white/12 bg-white/10 text-white/80 hover:bg-white/14'
                  : 'border-[#4f351f]/10 bg-white/70 text-[#584b3c] hover:bg-white/90'
              }`}
              aria-label="Close insight"
            >
              ×
            </button>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-2">
            {[
              ['📓', 'Capt’n’s Log', 'Saved to your history'],
              ['🧠', 'Roddo', 'Water signal saved'],
              ['🐟', 'Pattern', 'Species clue stored'],
              [insight.wasShared ? '🏝️' : '🧭', insight.wasShared ? 'Baleen Bay' : 'Bite Plan', insight.wasShared ? 'Shared with the Bay' : 'Ready for next trip'],
            ].map(([emoji, title, text]) => (
              <div key={title} className={`rounded-[12px] border p-2.5 ${softCardClass}`}>
                <div className="text-xl" aria-hidden="true" >{emoji}</div>
                <p className={`mt-1 text-[11px] font-black uppercase tracking-[0.16em] ${isDarkMode ? 'text-white/72' : 'text-[#8a6b3f]'}`}>{title}</p>
                <p className={`mt-1 text-xs font-bold leading-snug ${mutedText}`}>{text}</p>
              </div>
            ))}
          </div>

          <div className={`mt-4 overflow-hidden rounded-[16px] border ${softCardClass}`}>
            <div className={`border-b p-4 ${isDarkMode ? 'border-white/9' : 'border-[#4f351f]/8'}`}>
              <p className={`text-[11px] font-black uppercase tracking-[0.22em] ${isDarkMode ? 'text-[#d8e1ff]/72' : 'text-[#8a6b3f]'}`}>You logged</p>
              <div className="mt-3 grid gap-2.5 text-sm font-extrabold">
                <div className="flex items-center justify-between gap-3">
                  <span className={mutedText}>Species</span>
                  <span className={`text-right ${strongText}`}>{catchEntry.species || 'Fish'}</span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className={mutedText}>Time</span>
                  <span className={`text-right ${strongText}`}>{insight.catchTime} • {insight.bucket}</span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className={mutedText}>Water</span>
                  <span className={`truncate text-right ${strongText}`}>{insight.location}</span>
                </div>
              </div>
            </div>

            <div className={`border-b p-4 ${isDarkMode ? 'border-white/9 bg-[#8A70F2]/[0.07]' : 'border-[#4f351f]/8 bg-[#2F80ED]/[0.055]'}`}>
              <p className={`text-[11px] font-black uppercase tracking-[0.22em] ${isDarkMode ? 'text-[#C9C2FF]' : 'text-[#2168C9]'}`}>🧠 Pattern noted</p>
              <ul className={`mt-3 space-y-2.5 text-sm font-bold leading-snug ${isDarkMode ? 'text-white/92' : 'text-[#26323b]'}`}>
                {insight.patternLines.map((line) => (
                  <li key={line} className="flex gap-2.5">
                    <span aria-hidden="true" className="mt-[0.34rem] h-1.5 w-1.5 shrink-0 rounded-full bg-[#2F80ED] dark:bg-[#67D8E8]" />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className={`p-4 ${isDarkMode ? 'bg-[#F2B84B]/[0.08]' : 'bg-[#FFC94A]/[0.10]'}`}>
              <p className={`text-[11px] font-black uppercase tracking-[0.22em] ${isDarkMode ? 'text-amber-100/78' : 'text-[#8a650b]'}`}>🎯 Try next</p>
              <div className={`mt-3 space-y-2.5 text-sm font-bold leading-snug ${isDarkMode ? 'text-white/92' : 'text-[#26323b]'}`}>
                <div className="flex gap-2.5">
                  <span aria-hidden="true" className="mt-[0.34rem] h-1.5 w-1.5 shrink-0 rounded-full bg-[#F2B84B]" />
                  <span>{insight.lure}</span>
                </div>
                <div className="flex gap-2.5">
                  <span aria-hidden="true" className="mt-[0.34rem] h-1.5 w-1.5 shrink-0 rounded-full bg-[#F2B84B]" />
                  <span>{insight.nextWindow}</span>
                </div>
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="mt-4 flex w-full items-center justify-center gap-2 rounded-[12px] border border-[#E5AA22] bg-[#FFC94A] px-4 py-3 text-sm font-black text-[#3c2a10] shadow-[0_5px_14px_rgba(181,126,24,0.12)] active:translate-y-[1px]"
          >
            <span aria-hidden="true">🧭</span>
            <span>{insight.wasShared ? 'Open the Bay signal' : 'Back to Big Blue'}</span>
          </button>

          <div className={`mt-3 flex items-center justify-between gap-3 rounded-[14px] border px-3 py-2.5 ${softCardClass}`}>
            <label className={`flex min-w-0 cursor-pointer items-center gap-2.5 text-left text-[11px] font-black tracking-[0.02em] ${mutedText}`}>
              <input
                type="checkbox"
                checked={disableAfterCatch}
                onChange={(event) => handleDisableToggle(event.currentTarget.checked)}
                className="h-4 w-4 shrink-0 accent-[#2F80ED] dark:accent-[#67D8E8]"
              />
              <span>Don’t show after catch</span>
            </label>

            <span className={`shrink-0 text-[10px] font-black uppercase tracking-[0.18em] ${isDarkMode ? 'text-[#C9C2FF]/70' : 'text-[#2168C9]/75'}`}>
              Roddo
            </span>
          </div>
        </div>
      </section>
    </div>,
    document.body,
  );
};

export default RoddoInsightCard;
